'use client';

import { Separator } from '@/components/ui/separator';
import { useSocket } from '@/context/socketContext';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { shouldEnableSentry } from '@/utils/common/environment';
import { SocketUser } from '@/utils/types/window';
import * as Sentry from '@sentry/nextjs';
import { persistor } from '@/redux/store';
import { signOut, useSession } from 'next-auth/react';
import React, { memo, useEffect, useRef, useState } from 'react';
import QuickMenuSettings from './QuickMenuSettings';
const UserDropDown: React.FC = () => {
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [isQuickMenuOpen, setIsQuickMenuOpen] = useState(false);
  const {
    status
  } = useSession();
  const {
    leaveAllModules
  } = useSocket();
  const user = useCurrentUser();
  const username = user.name ?? 'Guest';
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (status === 'loading') {
      return;
    }
    if (!user) {
      handleSignOut();
    }
  }, [status, user]);

  // Add or remove event listener for outside clicks
  useEffect(() => {
    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);
  const shortenUsername = (fullName: string): string => {
    const nameParts = fullName.split(' ');
    if (nameParts.length > 1) {
      return `${nameParts[0]} ${nameParts[1][0]}.`;
    }
    return fullName;
  };

  // Toggle dropdown visibility
  const toggleDropdown = (): void => {
    setIsDropdownOpen(prevState => !prevState);
  };

  // Handle clicks outside the dropdown
  const handleClickOutside = (event: MouseEvent): void => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsDropdownOpen(false);
    }
  };
  const handleSignOut = async () => {
    if (shouldEnableSentry()) {
      Sentry.setUser(null);
    }
    const callbackUrl = process.env.NEXT_PUBLIC_APP_URL ?? 'https://star.trsrentelco.com';
    // purge() clears only persist:root (redux state) — user-keyed dock prefs survive untouched
    await persistor.purge();
    localStorage.removeItem('firstLoginWindowShown');
    document.cookie.split(';').forEach(cookie => {
      const cookieName = cookie.split('=')[0].trim();
      const domain = window.location.hostname;
      const path = '/';
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}; domain=${domain}`;
    });
    leaveAllModules(user as SocketUser);
    signOut({
      callbackUrl: `${callbackUrl}/login`,
      redirect: true
    });
  };
  return <div ref={dropdownRef} className="relative" data-sentry-component="UserDropDown" data-sentry-source-file="UserDropDown.tsx">
      {/* Dropdown Toggle Button */}
      <button id="dropdownToggle" onClick={toggleDropdown} className="mx-3 my-2 flex items-center rounded-[4px] text-xs leading-[1] text-white md:border-0" aria-expanded={isDropdownOpen} aria-controls="dropdownMenu">
        <span>👋</span> Hello, {shortenUsername(username) || 'Guest'}
        <svg className="ms-3 h-2.5 w-2.5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 10 6" data-sentry-element="svg" data-sentry-source-file="UserDropDown.tsx">
          <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M1 1l4 4 4-4" data-sentry-element="path" data-sentry-source-file="UserDropDown.tsx" />
        </svg>
      </button>

      <QuickMenuSettings isOpen={isQuickMenuOpen} onClose={() => setIsQuickMenuOpen(false)} data-sentry-element="QuickMenuSettings" data-sentry-source-file="UserDropDown.tsx" />

      {/* Dropdown Menu */}
      {isDropdownOpen && <div id="dropdownMenu" className="absolute right-0 z-10 w-44 divide-y divide-gray-100 rounded-lg bg-white shadow">
          <ul className="py-2 text-sm text-gray-700 dark:text-gray-200">
            <li>
              <button className="w-full px-4 py-2 text-start hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white">
                Preferences
              </button>
            </li>
            <li>
              <button onClick={() => {
            setIsDropdownOpen(false);
            setIsQuickMenuOpen(true);
          }} className="w-full px-4 py-2 text-start hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white">
                Quick Menu Layout
              </button>
            </li>
            <Separator />
            <li>
              <button className="w-full px-4 py-2 text-start hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white">
                System Settings
              </button>
            </li>
            <li>
              <button className="w-full px-4 py-2 text-start hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white">
                Admin Settings
              </button>
            </li>
            <Separator />
            <li>
              <button onClick={handleSignOut} className="w-full cursor-pointer px-4 py-2 text-start hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white">
                Log Out
              </button>
            </li>
          </ul>
        </div>}
    </div>;
};
export default memo(UserDropDown);